import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  MagnifyingGlassIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

import { Card, Badge, Spinner, Button } from '@realvisor/ui';
import { useLeads } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';

function formatPrice(value: number | null | undefined): string {
  if (!value) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Kč`;
  if (value >= 1000) return `${Math.round(value / 1000)}K Kč`;
  return `${value.toLocaleString()} Kč`;
}

function formatPercent(value: number | null | undefined): string {
  if (!value) return '-';
  return `${value.toFixed(2)} %`;
}

export default function Mortgages() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  
  const { currentProject, isAllProjects } = useProject();
  const projectId = isAllProjects ? undefined : currentProject?.id;

  // Načíst leady které mají hypoteční data v sourcePayload
  const { data: leadsData, isLoading } = useLeads({
    projectId,
    search: search || undefined,
    page,
    limit: 20,
  });

  // Filtrovat pouze leady s hypotečními daty
  const mortgageLeads = leadsData?.data?.filter(
    (lead: any) => lead.sourcePayload?.mortgage || lead.sourcePayload?.source === 'mortgage_form'
  ) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hypotéky</h1>
          <p className="text-sm text-gray-500">
            Přehled hypotečních kalkulací z formulářů
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat podle jména nebo emailu..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : mortgageLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Kontakt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Cena nemovitosti
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Výše úvěru
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Měsíční splátka
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Úrok
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Doba / Fixace
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Projekt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mortgageLeads.map((lead: any) => {
                  const mortgage = lead.sourcePayload?.mortgage;
                  const contact = lead.contact;
                  const fullName = contact
                    ? `${contact.firstName} ${contact.lastName || ''}`.trim()
                    : 'Neznámý';

                  return (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                            <BanknotesIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{fullName}</p>
                            <p className="text-sm text-gray-500">
                              {contact?.email || contact?.phone || '-'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {mortgage?.propertyPrice
                          ? formatPrice(mortgage.propertyPrice)
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {mortgage?.loanAmount ? (
                          <span className="font-semibold text-blue-600">
                            {formatPrice(mortgage.loanAmount)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {mortgage?.monthlyPayment ? (
                          <span className="font-semibold text-green-600">
                            {formatPrice(mortgage.monthlyPayment)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {mortgage?.interestRate
                          ? formatPercent(mortgage.interestRate)
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {mortgage?.loanTerm && mortgage?.fixation
                          ? `${mortgage.loanTerm} let / ${mortgage.fixation} let`
                          : mortgage?.loanTerm
                          ? `${mortgage.loanTerm} let`
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {lead.project ? (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{
                              backgroundColor: `${lead.project.color || '#3B82F6'}20`,
                              color: lead.project.color || '#3B82F6',
                            }}
                          >
                            {lead.project.name}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="text-primary-600 hover:underline text-sm"
                        >
                          Detail
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <CalculatorIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Žádné hypoteční kalkulace nenalezeny</p>
            <p className="text-sm text-gray-400 mt-1">
              Hypotéky se zobrazí po vyplnění hypotečního formuláře
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
